import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { Button } from '@/shared/components/ui/button';
import { KpiSection } from '../components/KpiSection';
import { ViewTabs } from '../components/ViewTabs';
import { MemberSearch } from '../components/MemberSearch';
import { PerformanceTimeline } from '../components/PerformanceTimeline';
import { PerformanceTableView } from '../components/PerformanceTableView';
import { PerformanceChartView } from '../components/PerformanceChartView';
import { ProjectAccordion } from '../components/ProjectAccordion';
import { useLaborPerformanceSummary } from '../hooks/useLaborPerformanceSummary';
import { useLaborPerformanceTimeline } from '../hooks/useLaborPerformanceTimeline';
import { useLaborPerformanceTable } from '../hooks/useLaborPerformanceTable';
import { useProjectsPerformance } from '../hooks/useProjectsPerformance';
import { usePeriod, useActiveTab, useMemberSearch } from '../hooks/useLaborPerformanceStore';
import { PERFORMANCE_TAB } from '../types';

export default function LaborPerformancePage() {
  const { t } = useTranslation('laborPerformance');

  const { periodType, periodStart, periodEnd, navigatePeriod, setPeriodType } = usePeriod();
  const { activeTab, setActiveTab } = useActiveTab();
  const { memberSearch, setMemberSearch } = useMemberSearch();

  const { data: summary, isLoading: summaryLoading } = useLaborPerformanceSummary(
    periodStart,
    periodEnd,
  );

  const timelineFilters = { periodStart, periodEnd, memberSearch: memberSearch || undefined };
  const {
    data: timelineData = [],
    isLoading: timelineLoading,
    isError: timelineError,
    refetch: refetchTimeline,
  } = useLaborPerformanceTimeline(timelineFilters);

  const tableFilters = { periodStart, periodEnd, memberSearch: memberSearch || undefined };
  const {
    data: tableData = [],
    isLoading: tableLoading,
    isError: tableError,
    refetch: refetchTable,
  } = useLaborPerformanceTable(tableFilters);

  const {
    data: projectsData = [],
    isLoading: projectsLoading,
    isError: projectsError,
    refetch: refetchProjects,
  } = useProjectsPerformance(periodStart, periodEnd);

  const filteredTimeline = memberSearch
    ? timelineData.filter((e) => e.memberName.toLowerCase().includes(memberSearch.toLowerCase()))
    : timelineData;

  const filteredTable = memberSearch
    ? tableData.filter((e) => e.memberName.toLowerCase().includes(memberSearch.toLowerCase()))
    : tableData;

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div data-testid="labor-performance-page" className="flex flex-col gap-6 p-4 md:p-6">
        <Toolbar className="pt-0">
          <Toolbar.Info title={t('page.title')} description={t('page.subtitle')} />
          <Toolbar.Actions>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              data-testid="labor-performance-export-btn"
            >
              <Upload className="h-4 w-4" />
              {t('page.exportBtn')}
            </Button>
          </Toolbar.Actions>
        </Toolbar>

        <KpiSection
          summary={summary}
          isLoading={summaryLoading}
          periodType={periodType}
          periodStart={periodStart}
          periodEnd={periodEnd}
          onNavigate={navigatePeriod}
          onChangePeriodType={setPeriodType}
        />

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <ViewTabs activeTab={activeTab} onChange={setActiveTab} />
          <MemberSearch onChange={setMemberSearch} className="w-full md:w-64" />
        </div>

        {activeTab === PERFORMANCE_TAB.TIMELINE && (
          <>
            <PerformanceTimeline
              entries={filteredTimeline}
              periodStart={periodStart}
              periodEnd={periodEnd}
              isLoading={timelineLoading}
              isError={timelineError}
              onRetry={refetchTimeline}
            />
            <div className="mt-4">
              <ProjectAccordion
                projects={projectsData}
                periodStart={periodStart}
                periodEnd={periodEnd}
                isLoading={projectsLoading}
                isError={projectsError}
                onRetry={refetchProjects}
              />
            </div>
          </>
        )}

        {activeTab === PERFORMANCE_TAB.TABLE && (
          <PerformanceTableView
            entries={filteredTable}
            isLoading={tableLoading}
            isError={tableError}
            onRetry={refetchTable}
          />
        )}

        {activeTab === PERFORMANCE_TAB.CHART && (
          <PerformanceChartView
            tableEntries={filteredTable}
            timelineEntries={filteredTimeline}
            periodStart={periodStart}
            periodEnd={periodEnd}
            isLoading={timelineLoading || tableLoading}
            isError={timelineError || tableError}
            onRetry={refetchTimeline}
          />
        )}
      </div>
    </FeatureErrorBoundary>
  );
}
